import React, { ReactElement, useMemo } from 'react';
import { Divider, FlexboxGrid, Row, Text } from 'rsuite';
import { TaxableReceiptsBreakdown } from './TaxableReceiptsBreakdown';
import {
  CollectionAgreementBucket,
  TaxableActivity,
  TaxableReceipt,
} from 'src/types/tax';
import {
  TaxConfiguration,
  TaxConfigurationInformation,
} from 'src/types/configuration';
import { TaxBreakdownItem } from './TaxBreakdownItem';

interface TaxBreakdownProviderProps {
  bucket: CollectionAgreementBucket;
  configuration: TaxConfiguration;
  taxableActivity: TaxableActivity;
}

const calculateTotalTaxDueForProvider = (
  taxConfig: Array<TaxConfigurationInformation>,
  provider: TaxableActivity
) => {
  return taxConfig.reduce((total, item) => {
    const providerTaxType = provider.taxTypes[item.key];
    if (!providerTaxType) return total;
    return providerTaxType.total + total;
  }, 0);
};

export const TaxBreakdownProvider = ({
  bucket,
  configuration,
  taxableActivity,
}: TaxBreakdownProviderProps): ReactElement => {
  const receipts = useMemo(() => {
    return Object.entries(taxableActivity.taxTypes) as Array<
      [string, TaxableReceipt]
    >;
  }, [taxableActivity]);

  return (
    <FlexboxGrid
      as={Row}
      style={{ margin: '8px 0' }}
    >
      <FlexboxGrid.Item colspan={24}>
        <Text
          as="h4"
          style={{ padding: '0 0 4px' }}
        >
          {bucket.label || 'Bucket not found'}
        </Text>
      </FlexboxGrid.Item>
      <TaxBreakdownItem
        title={configuration?.gross_receipts_label || ''}
        value={taxableActivity.sales}
        infoText={configuration?.gross_receipts_description}
      />
      {configuration?.deductions?.enable && (
        <TaxBreakdownItem
          title={configuration.deductions.label || ''}
          value={taxableActivity.deductions}
          infoText={configuration.deductions.text}
        />
      )}
      <TaxBreakdownItem
        title={configuration.taxable_receipts_label || ''}
        value={taxableActivity.taxableReceipts}
        bold
      />

      <FlexboxGrid.Item
        colspan={24}
        style={{ margin: '8px 0' }}
        as={Row}
      >
        {receipts.map((entry, index) => {
          const config = configuration.tax_config.find(
            (c) => c.key === entry[0]
          );
          return (
            <TaxableReceiptsBreakdown
              testId={`taxable-receipts-${entry[0]}`}
              key={entry[0]}
              receipt={entry[1]}
              dark={index % 2 === 0}
              configuration={config}
            />
          );
        })}
      </FlexboxGrid.Item>
      <TaxBreakdownItem
        title={`Total for ${bucket.label}`}
        value={calculateTotalTaxDueForProvider(
          configuration.tax_config,
          taxableActivity
        )}
        bold
      />
      <FlexboxGrid.Item colspan={24}>
        <Divider />
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};
